import "../../styles/conversation-drawer.css";
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import { useDispatch } from "react-redux";
import { addConversation } from "../../store/conversation.tsx";
import ConversationDrawerActions from "./ConversationDrawerActions.tsx";
import ConversationDrawerSection from "./ConversationDrawerSection.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import CloseDrawerIcon from "../icons/CloseDrawerIcon.tsx";
import OpenDrawerIcon from "../icons/OpenDrawerIcon.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { Box, useMediaQuery } from "@mui/material";
import AddConversationIcon from "../icons/AddConversationIcon.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import ViewSubscriptionButton from "../billing/ViewSubscriptionButton.tsx";

interface AppBarProps extends MuiAppBarProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "block",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  margin: "0px",
}));

const ConversationDrawer: React.FC<AppBarProps> = (props) => {
  const theme = useTheme();
  const conversations = useTypedSelector(
    (state) => state.conversationState.conversations,
  );

  const dispatch = useDispatch();

  const handleAddConversation = async () => {
    dispatch(addConversation() as any);
  };

  const handleDrawerOpen = () => {
    props.setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    props.setOpenDrawer(false);
  };

  return (
    <Box>
      <Drawer
        PaperProps={{
          sx: {
            backgroundColor: `${theme.palette.primary.main}`,
            color: `${theme.palette.common.black}`,
            display: "flex",
            flexDirection: "column",
          },
        }}
        sx={{
          height: "100vh",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: SETTINGS.drawerWidth,
            boxSizing: "border-box",
          },
          padding: 0,
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
        variant="persistent"
        anchor="left"
        open={props.openDrawer}
        id="conversation-drawer"
      >
        <DrawerHeader
          sx={{
            minHeight: "50px !important",
            p: "14px 14px",
          }}
        >
          <IconButton
            onClick={handleDrawerClose}
            sx={{
              float: "left",
              fontSize: theme.typography.h5.fontSize,
              top: "auto",
            }}
          >
            <CloseDrawerIcon color={theme.palette.common.black} />
          </IconButton>
          <IconButton
            onClick={() => handleAddConversation()}
            sx={{
              float: "right",
              fontSize: theme.typography.h5.fontSize,
            }}
          >
            <AddConversationIcon color={theme.palette.common.black} />
          </IconButton>
        </DrawerHeader>
        <ConversationDrawerActions />
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <ConversationDrawerSection
            conversations={conversations.pinned}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations pinned by the user",
                message: "Pinned conversations",
              }),
            )}
            toggleConversation={false}
          />
          <ConversationDrawerSection
            conversations={conversations.last_7_days}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations  created in the last 7 days",
                message: "Last 7 days",
              }),
            )}
            toggleConversation={true}
          />
          <ConversationDrawerSection
            conversations={conversations.last_30_days}
            title={i18n._(
              msg({
                context:
                  "Header for a section with conversations  created in the last 30 days",
                message: "Last 30 days",
              }),
            )}
            toggleConversation={true}
          />
          <ConversationDrawerSection
            conversations={conversations.other}
            title={"2024"}
            toggleConversation={true}
          />
        </Box>
        <ViewSubscriptionButton />
      </Drawer>
      {useMediaQuery(theme.breakpoints.up("md")) ? (
        <IconButton
          onClick={handleDrawerOpen}
          sx={{
            left: "14px",
            top: "14px",
            position: "absolute",
          }}
        >
          <OpenDrawerIcon
            color={theme.palette.secondary.main}
            fontSize={theme.typography.h5.fontSize}
          />
        </IconButton>
      ) : (
        <Box />
      )}
    </Box>
  );
};

export default ConversationDrawer;
